import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import { AttachmentsPreview, DATE_FORMATS_FNS, ReactTable } from "components";
import "components/AdvisedCriticalHistory/components/AdvisedCriticalHistoryTable/AdvisedCriticalHistoryTable.scss";
import { AdvisedCriticalHistoryActions } from "components/AdvisedCriticalHistory/components/AdvisedCriticalHistoryTable/components/AdvisedCriticalHistoryActions";
import { useAdvisedCriticalItemsHistory } from "components/AdvisedCriticalHistory/components/AdvisedCriticalHistoryTable/hooks";
import { parseQuestionResultAttachments } from "modules/AppointmentDetails/utils";
import { PAGE_SIZE, getTyreSeasonIcon } from "utils/common";
import { formatDate, isDate } from "utils/dateHelperFunctions";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { Appointment, QuestionResult } from "models";

type AdvisedCriticalItemsHistoryTableProps = {
  carId?: number;
  appointment?: Appointment;
  appointmentId?: number;
};

const columnHelper = createColumnHelper<QuestionResult>();

export const AdvisedCriticalHistoryTable = ({ appointment, appointmentId, carId }: AdvisedCriticalItemsHistoryTableProps) => {
  const { t } = useTranslation();

  const { advisedCriticalHistory, loading, refetchAdvisedCriticalHistory, setPage, showHiddenItems, toggleHiddenItems, page } = useAdvisedCriticalItemsHistory({
    appointment_id: appointmentId,
    car_id: carId
  });

  useEffect(() => {
    if (!loading && !advisedCriticalHistory?.question_results.length && page > 1) setPage(page - 1);
  }, [advisedCriticalHistory, page, loading]);

  const totalPages = advisedCriticalHistory?.nb_pages || 1;

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: totalPages }); // TODO: this can't be working or be needed as it's gonna be always 1

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    setPagination(currentPagination => {
      const pagination = typeof updater === "function" ? updater(currentPagination) : updater;
      setPage(pagination.pageIndex + 1);
      return pagination;
    });
  };

  const columns = [
    columnHelper.accessor(row => row, {
      id: "date",
      header: t("v8_appointment_date", "Appointment Date"),
      cell: ({ row: { original } }) => (
        <span>
          {original.time_car_app && (isDate(original.time_car_app) || !original.time_car_app.startsWith("0001-01-01T00:00:00"))
            ? formatDate(original.time_car_app, DATE_FORMATS_FNS.dateMonthYear)
            : ""}
        </span>
      ),
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "wo",
      header: "WO #",
      cell: ({ row: { original } }) => (
        <span>
          <Link to={`/appointments/${original.appointment_id}`} target="_blank">
            <Icon className="arrow up right from square" />
            {original.wo_nr}
          </Link>
        </span>
      ),
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "title",
      header: t("v8_title", "Title"),
      cell: ({ row: { original } }) => (
        <span>
          {original.tyre_id && original.tyre && getTyreSeasonIcon(original.tyre.season)} {original.title}
        </span>
      ),
      size: 200
    }),
    columnHelper.accessor(row => row, {
      id: "remarks",
      header: t("v8_remarks", "Remarks"),
      cell: ({ row: { original } }) => <span>{original.raw || ""}</span>,
      size: 200
    }),
    columnHelper.accessor(row => row, {
      id: "action",
      header: () => (
        <div className="header-action-buttons">
          <Popup
            wide
            basic
            content={t("v8_show_hidden_items", "Show hidden items")}
            trigger={
              <div className="hidden-items-button" onClick={toggleHiddenItems}>
                <Icon className={`clock rotate left ${showHiddenItems ? "green" : "grey"}`} />
              </div>
            }
          />
        </div>
      ),
      cell: ({ row: { original, toggleExpanded } }) => (
        <div className="action-container">
          <AdvisedCriticalHistoryActions
            questionResult={original}
            appointment={appointment}
            toggleShowMedia={() => toggleExpanded()}
            onUpdateHiddenItems={() => refetchAdvisedCriticalHistory()} // TODO: handle it properly instead of refetching
            onInterventionAddSuccess={() => refetchAdvisedCriticalHistory()} // TODO: handle it properly instead of refetching
          />
        </div>
      ),
      size: 100
    })
  ];

  return (
    <div className="AdvisedCriticalHistory-AdvisedCriticalTable mt-15">
      <ReactTable
        isFetching={loading}
        columns={columns}
        data={advisedCriticalHistory?.question_results}
        pageCount={totalPages}
        pagination={totalPages > 1 ? pagination : undefined}
        onPaginationChange={handlePaginationChange}
        totalItems={advisedCriticalHistory?.nb_count ?? 0}
        pageSize={PAGE_SIZE.PageSize10}
        renderExpandedComponent={row => {
          // TODO: what is this about ? we cannot expand these rows ? remove this expand prop here if it's not used
          const questionResult = row.original;
          const attachments = parseQuestionResultAttachments(questionResult);
          return <AttachmentsPreview attachments={attachments} />;
        }}
      />
    </div>
  );
};
